import { service } from '@ember/service';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import type { ReportSource } from 'garaje/models/approval-status';
import type ScreenerResult from 'garaje/models/screener-result';
import type { ApprovalStatus } from 'garaje/models/visit-approval';
import { isNonEmptyArray } from 'garaje/utils/type-utils';

import type { GetCanReviewOrReadBlocklistMatchModel } from './security';

export const sourceToAbilityMap: Partial<Record<ReportSource, string>> = {
  blacklist: 'blocklist',
  'property-invite-approval': 'approval-required-invites', // for property walk-in visitors that need approval; for now, use the same ability as for approving outgoing invites from Visitors
  'requires-approval': 'approval-required-invites',
  'visit-workflows': 'ava-approval',
};

/**
 * This is a quick hack to deal with the fact that both Locations and Properties (Zones)
 * can have blocklists, but (at the moment) no other screeners are available to Properties.
 *
 * If you are here because you are about to add another screener to the list of ones that are
 * allowed for properties, **DO NOT DO IT!** Now is the time to step back and handle this the
 * right way, probably by having the backend include something on _all_ screeners indicating
 * their source (location, property, etc).
 *
 * Why is this array/check here, rather than in the appropriate ability for the screener?
 * I'm glad you asked - it's because at the time this code was written, I wasn't able to get
 * a comprehensive list of all the screeners that exist.
 * PG 2022-06-30
 */
const SCREENERS_AVAILABLE_TO_PROPERTIES = ['blocklist'];

export default class EntryApprovalAbility extends Ability {
  declare model?: GetCanReviewOrReadBlocklistMatchModel;

  @service declare abilities: AbilitiesService;

  get canReviewSomeReviewable(): boolean {
    if (this.model?.visitApproval)
      return this.#checkApprovalPermission({ checkAll: false, type: 'review', status: 'review' });
    return false;
  }

  get canReviewSome(): boolean {
    if (this.model?.visitApproval) return this.#checkApprovalPermission({ checkAll: false, type: 'review' });
    return false;
  }

  get canViewSome(): boolean {
    if (this.model?.visitApproval) return this.#checkApprovalPermission({ checkAll: false, type: 'view' });
    return false;
  }

  get canReviewAllReviewable(): boolean {
    if (this.model?.visitApproval)
      return this.#checkApprovalPermission({ checkAll: true, type: 'review', status: 'review' });
    return false;
  }

  get canReviewAllFailed(): boolean {
    // A key difference between checking status vs decision, is that a status can change (review->approved|denied)
    // while decision is static.
    if (this.model?.visitApproval)
      return this.#checkApprovalPermission({ checkAll: true, type: 'review', resultDecision: 'fail' });
    return false;
  }

  get canReview(): boolean {
    if (this.model?.visitApproval) {
      return this.#checkApprovalPermission({ checkAll: true, type: 'review', resultDecision: 'fail' });
    }

    if (!Array.isArray(this.model?.report)) {
      return false;
    }

    return this.model.report
      .filter((report) => report.reviewable)
      .every((report) => {
        const source = sourceToAbilityMap[report.source] || report.source;
        if (this.model?.context === 'property' && !SCREENERS_AVAILABLE_TO_PROPERTIES.includes(source)) {
          return false;
        }
        return this.abilities.can(`review ${source}`, this.model);
      });
  }

  get canView(): boolean {
    if (this.model?.visitApproval)
      return this.#checkApprovalPermission({ checkAll: true, type: 'view', status: 'review' });

    if (!Array.isArray(this.model?.report)) {
      return false;
    }

    return this.model.report
      .filter((report) => report.reviewable)
      .every((report) => {
        const source = sourceToAbilityMap[report.source] || report.source;
        if (this.model?.context === 'property' && !SCREENERS_AVAILABLE_TO_PROPERTIES.includes(source)) {
          return false;
        }
        return this.abilities.can(`view ${source}`, this.model);
      });
  }

  #checkApprovalPermission(options: {
    status?: ApprovalStatus;
    resultDecision?: string;
    checkAll: boolean;
    type: 'review' | 'view';
  }): boolean {
    const { checkAll, type, status, resultDecision } = options;

    const canFunc = (result: ScreenerResult) => {
      const source = sourceToAbilityMap[result.screenerSource] || result.screenerSource;

      if (this.model?.context === 'property' && !SCREENERS_AVAILABLE_TO_PROPERTIES.includes(source)) {
        return false;
      }
      return this.abilities.can(`${type} ${source}`, this.model);
    };

    const screenerResults = this.model!.visitApproval!.screenerResults.filter((result) => {
      return (result.status === status || !status) && (result.decision === resultDecision || !resultDecision);
    });

    // `[].every()` is true, so an empty set would grant authority without ever calling canFunc.
    if (!isNonEmptyArray(screenerResults)) return false;

    const func = checkAll ? 'every' : 'some';
    return screenerResults[func](canFunc);
  }
}
